import { useState, useEffect, forwardRef, useImperativeHandle } from "react"
import { TextField, Button, Tooltip, IconButton, Card, CardHeader, CardContent } from "@mui/material"
import DeleteIcon from "@mui/icons-material/Delete"
import { CommonConstant } from "shared/constants/common-constant"
import { rootStore } from "stores/root-store"
import AddIcon from "@mui/icons-material/Add"
import { CustomDataGrid } from "components/common-styles"
import { genId } from "utils/app-helper"
import { GlobalData } from "shared/global-data"
import VirtualizedAutocomplete from "components/common/virtualized-autocomplete"

const BomEditComponents = forwardRef(({ bomInfo, isSubmitting }, ref) => {
  const columns = [
    {
      field: "build_sequence",
      headerName: "Build Sequence",
      width: 150,
      renderCell: params => Number(params.value).toFixed(3),
    },
    {
      field: "item",
      headerName: "Item",
      minWidth: 250,
      flex: 1,
      renderCell: params => (
        <VirtualizedAutocomplete
          id={`sub_item${params.row.build_sequence}`}
          value={params.value}
          isOptionEqualToValue={(option, value) => option.id === value}
          onChange={(event, x) => onPropChanged(params.row["id"], "item", x.id)}
          options={GlobalData.ProductListSelection}
          renderInput={paramsInner => <TextField {...paramsInner} error={Boolean(params.row.validation?.item)} helperText={params.row.validation?.item} />}
        />
      ),
    },
    {
      field: "item_desc",
      headerName: "Item Description",
      minWidth: 250,
      flex: 1,
      renderCell: params => <TextField value={params.value} fullWidth id="item_desc" InputProps={{ readOnly: true }} />,
    },
    {
      field: "qty_needed_per_item",
      headerName: "Qty Per Item",
      width: 200,
      renderCell: params => (
        <TextField
          value={params.value}
          type="number"
          id={`qty_needed_per_item${params.row.build_sequence}`}
          onChange={event => onPropChanged(params.row.id, "qty_needed_per_item", event.target.value)}
          error={Boolean(params.row.validation.qty_needed_per_item)}
          helperText={params.row.validation.qty_needed_per_item}
          inputProps={{ min: 0 }}
        />
      ),
    },
    {
      field: "actions",
      headerName: "",
      width: 80,
      renderCell: params =>
        products.length > 1 && (
          <Tooltip placement="top" title="Delete">
            <IconButton id="btnDeleteItem" aria-label="actions" onClick={() => onDeleteClicked(params.row.build_sequence)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        ),
    },
  ]
  const [products, setProducts] = useState([])

  useEffect(() => {
    if (products.length == 0) {
      setProducts([...products, createProduct()])
    }
  }, [products])

  useEffect(() => {
    if (bomInfo?.products) {
      const result = [...bomInfo.products]
      result.forEach(p => {
        if (p.item) {
          const productItem = GlobalData.ProductListSelection.find(x => x.id?.toUpperCase() === p.item?.toUpperCase())
          p.item_desc = productItem?.description
        }

        p.validation = {}
        p.id = genId()
        p.user_stamp = rootStore.userName
        p.company = rootStore.company
      })
      setProducts(result)
    }
  }, [bomInfo])

  useImperativeHandle(ref, () => ({
    getProductsIfValid() {
      if (validateData()) return products

      return null
    },
  }))

  const getErrorMessageOfProp = (newProducts, id, prop, val) => {
    if (prop === "item") {
      if (!val) return "Item is required."
      else {
        if (!rootStore.isProductAvailable(val)) return `${val.toUpperCase()} is not available.`
        const found = newProducts.find(y => y.id !== id && y.product === val)
        if (found) return `${val.toUpperCase()} already exists`
      }
    } else {
      if (prop === "qty_needed_per_item" && (!val || parseFloat(val) == 0)) {
        return "Qty is required."
      }

      if (parseFloat(val) < 0) return "Invalid number."
    }

    return ""
  }

  const onPropChanged = async (id, prop, val) => {
    let newProducts = [...products]
    const item = newProducts.find(x => x.id === id)
    if (item) {
      if (item.internal_bom_detail_num) item.action = "CHANGE"

      item[prop] = val
      item.validation[prop] = getErrorMessageOfProp(newProducts, id, prop, val)

      if (prop == "item") {
        const productItem = GlobalData.ProductListSelection.find(x => x.id?.toUpperCase() === val?.toUpperCase())
        item["item_desc"] = productItem?.description
      }

      setProducts(newProducts)
    }
  }

  const onDeleteClicked = build_sequence => {
    const newProducts = [...products]
    const item = newProducts.find(x => x.build_sequence === build_sequence)
    if (item) {
      if (item.internal_bom_detail_num) {
        item.action = "DELETE"
        setProducts(newProducts)
      } else {
        setProducts(newProducts.filter(x => x.build_sequence !== build_sequence))
      }
    }
  }

  const validateData = () => {
    let isValid = true
    const newProducts = [...products].filter(item => item.action !== "DELETE")
    newProducts.forEach(x => {
      if (!x.validation) {
        x.validation = {}
      }

      x.validation.item = getErrorMessageOfProp(newProducts, x.id, "item", x.item)
      x.validation.qty_needed_per_item = getErrorMessageOfProp(newProducts, x.id, "qty_needed_per_item", x.qty_needed_per_item)

      if (x.validation.item || x.validation.qty_needed_per_item) isValid = false
    })

    setProducts(newProducts)

    return isValid
  }

  const getLineNumberMaxValue = data => {
    const max = Math.max(...data.map(o => o.build_sequence), 0)
    return max + 1
  }

  const generateLineNum = () => {
    const realProducts = products.filter(item => item.action !== "DELETE")
    return getLineNumberMaxValue(realProducts)
  }

  const createProduct = () => {
    return {
      action: "NEW",
      item: "",
      qty_needed_per_item: 0,
      internal_bom_detail_num: 0,
      build_sequence: generateLineNum(),
      validation: {},
      id: genId(),
      user_stamp: rootStore.userName,
      company: rootStore.company,
    }
  }

  const handleAddProduct = () => {
    if (validateData()) {
      const newProducts = [...products]
      const product = createProduct()
      setProducts([...newProducts, product])
    }
  }

  return (
    <Card id="cardComponents">
      <CardHeader
        title="Components"
        action={
          <Button id="btnAddItem" color="grey" variant="contained" onClick={handleAddProduct} disabled={isSubmitting} startIcon={<AddIcon />}>
            Add Item
          </Button>
        }
      />
      <CardContent>
        <CustomDataGrid
          getRowId={row => row.id}
          rows={products.filter(x => x.action !== "DELETE")}
          columns={columns}
          initialState={{
            sorting: {
              sortModel: [{ field: "build_sequence", sort: CommonConstant.ASCENDING }],
            },
            pagination: {
              paginationModel: {
                pageSize: 15,
              },
            },
          }}
          pageSizeOptions={[10, 15, 20, 50]}
          rowHeight={85}
          disableRowSelectionOnClick
        />
      </CardContent>
    </Card>
  )
})

export default BomEditComponents
